// src/helpers/comisionEsquemas.js
// Traduce los esquemas de devengo de comisiones a planes concretos de hitos.
//
// Cada hito generado tiene la forma:
//   {
//     etiqueta: string,           // visible en UI y reportes
//     trigger: string,            // ver TRIGGER_TYPES más abajo
//     monto: number,              // pesos exactos que se devengan al cumplir el trigger
//     devengado: false,           // se marca true cuando procesarDevengoComision lo detecta
//     cxpId: null,                // se llena cuando se materializa la CxP
//     deudaPagoId: null,          // pago del cliente que disparó el devengo
//   }
//
// Triggers soportados:
//   "contrato.firmado"   → cumple en cuanto exista la deuda
//   "enganche.cobrado"   → cumple cuando la suma de pagos cubre el enganche
//   "mensualidad.N"      → cumple cuando se acumula la N-ésima mensualidad regular

export const TRIGGER_TYPES = Object.freeze({
  CONTRATO_FIRMADO: "contrato.firmado",
  ENGANCHE_COBRADO: "enganche.cobrado",
  MENSUALIDAD: "mensualidad.",
});

export const ESQUEMA_OPCIONES = [
  { value: "enganche-100", label: "100% al cobrar enganche" },
  { value: "enganche-50-mensualidades-50", label: "50% al enganche + 50% en N mensualidades" },
  { value: "escalonado-por-pagos", label: "30% al firmar + 30% al enganche + 40% en 6 pagos" },
  { value: "custom", label: "Personalizado (definir hitos manualmente)" },
];

const DEFAULT_MENSUALIDADES_50 = 5;
const DEFAULT_MENSUALIDADES_ESCALONADO = 6;

// Redondea a 2 decimales sin perder importes en el último hito.
const roundCent = (n) => Math.round(n * 100) / 100;

// Reparte un total en N partes iguales corrigiendo el residuo en la última.
function repartirIgual(total, n) {
  if (n <= 0) return [];
  const base = roundCent(total / n);
  const partes = Array(n - 1).fill(base);
  const acumulado = roundCent(base * (n - 1));
  partes.push(roundCent(total - acumulado));
  return partes;
}

const hito = (etiqueta, trigger, monto) => ({
  etiqueta,
  trigger,
  monto: roundCent(monto),
  devengado: false,
  cxpId: null,
  deudaPagoId: null,
});

/**
 * parseEsquemaComision
 *
 * @param {object} params
 * @param {string} params.esquema  uno de ESQUEMA_OPCIONES.value
 * @param {number} params.montoTotal  total bruto de la comisión en pesos
 * @param {number} [params.mensualidadesN]  cuántas mensualidades distribuir (depende del esquema)
 * @param {Array}  [params.hitosCustom]  sólo para esquema="custom"
 * @returns {object} { esquema, hitos: [...] }
 * @throws Error si el esquema no es válido o falta input requerido
 */
export function parseEsquemaComision({
  esquema,
  montoTotal,
  mensualidadesN,
  hitosCustom = [],
}) {
  const total = Number(montoTotal);
  if (!Number.isFinite(total) || total <= 0) {
    throw new Error("parseEsquemaComision: montoTotal debe ser un número positivo.");
  }

  switch (esquema) {
    case "enganche-100": {
      return {
        esquema,
        hitos: [hito("Enganche", TRIGGER_TYPES.ENGANCHE_COBRADO, total)],
      };
    }

    case "enganche-50-mensualidades-50": {
      const n = Number(mensualidadesN ?? DEFAULT_MENSUALIDADES_50);
      if (!Number.isInteger(n) || n < 1) {
        throw new Error("parseEsquemaComision: mensualidadesN debe ser entero positivo.");
      }
      const mitad = roundCent(total / 2);
      const restante = roundCent(total - mitad);
      const partes = repartirIgual(restante, n);
      return {
        esquema,
        hitos: [
          hito("Enganche", TRIGGER_TYPES.ENGANCHE_COBRADO, mitad),
          ...partes.map((m, i) =>
            hito(`Mes ${i + 1}`, `${TRIGGER_TYPES.MENSUALIDAD}${i + 1}`, m)
          ),
        ],
      };
    }

    case "escalonado-por-pagos": {
      const n = Number(mensualidadesN ?? DEFAULT_MENSUALIDADES_ESCALONADO);
      if (!Number.isInteger(n) || n < 1) {
        throw new Error("parseEsquemaComision: mensualidadesN debe ser entero positivo.");
      }
      const m30a = roundCent(total * 0.3);
      const m30b = roundCent(total * 0.3);
      const m40 = roundCent(total - m30a - m30b);
      const partes = repartirIgual(m40, n);
      return {
        esquema,
        hitos: [
          hito("Contrato firmado", TRIGGER_TYPES.CONTRATO_FIRMADO, m30a),
          hito("Enganche", TRIGGER_TYPES.ENGANCHE_COBRADO, m30b),
          ...partes.map((m, i) =>
            hito(`Mes ${i + 1}`, `${TRIGGER_TYPES.MENSUALIDAD}${i + 1}`, m)
          ),
        ],
      };
    }

    case "custom": {
      if (!Array.isArray(hitosCustom) || hitosCustom.length === 0) {
        throw new Error("parseEsquemaComision: 'custom' requiere hitosCustom no vacío.");
      }
      const hitos = hitosCustom.map((h, i) => {
        if (!h.etiqueta) throw new Error(`Hito ${i + 1}: falta etiqueta.`);
        if (!h.trigger) throw new Error(`Hito ${i + 1}: falta trigger.`);
        if (!isTriggerValido(h.trigger)) {
          throw new Error(`Hito ${i + 1}: trigger inválido "${h.trigger}".`);
        }
        const monto = Number(h.monto);
        if (!Number.isFinite(monto) || monto <= 0) {
          throw new Error(`Hito ${i + 1}: monto debe ser positivo.`);
        }
        return hito(h.etiqueta, h.trigger, monto);
      });
      const suma = roundCent(hitos.reduce((acc, x) => acc + x.monto, 0));
      if (Math.abs(suma - total) > 0.5) {
        // Tolerancia de 50 centavos por redondeo; cualquier diferencia mayor es error de captura.
        throw new Error(
          `parseEsquemaComision: la suma de hitos custom ($${suma}) no coincide con montoTotal ($${total}).`
        );
      }
      return { esquema, hitos };
    }

    default:
      throw new Error(`parseEsquemaComision: esquema desconocido "${esquema}".`);
  }
}

/** Valida que un string sea un trigger soportado. */
export function isTriggerValido(trigger) {
  if (trigger === TRIGGER_TYPES.CONTRATO_FIRMADO) return true;
  if (trigger === TRIGGER_TYPES.ENGANCHE_COBRADO) return true;
  if (trigger.startsWith(TRIGGER_TYPES.MENSUALIDAD)) {
    const n = Number(trigger.slice(TRIGGER_TYPES.MENSUALIDAD.length));
    return Number.isInteger(n) && n >= 1;
  }
  return false;
}

/**
 * Evalúa si un trigger se cumple dado el estado de la deuda.
 *
 * @param {string} trigger
 * @param {object} deuda  documento de la deuda (con enganche, mensualidad y pagos[])
 * @returns {object|null} { cumple: boolean, deudaPagoId: string | null }
 */
export function evaluarTrigger(trigger, deuda) {
  const pagos = Array.isArray(deuda?.pagos) ? deuda.pagos : [];
  const pagosPositivos = pagos.filter((p) => Number(p?.cantidad || 0) > 0);
  const enganche = Number(deuda?.enganche || 0);
  const mensualidad = Number(deuda?.mensualidad || 0);

  if (trigger === TRIGGER_TYPES.CONTRATO_FIRMADO) {
    return { cumple: !!deuda?.id, deudaPagoId: null };
  }

  if (trigger === TRIGGER_TYPES.ENGANCHE_COBRADO) {
    if (enganche <= 0) return { cumple: false, deudaPagoId: null };
    let acumulado = 0;
    for (const p of pagosPositivos) {
      acumulado += Number(p.cantidad || 0);
      if (acumulado >= enganche) {
        return { cumple: true, deudaPagoId: p.id || null };
      }
    }
    return { cumple: false, deudaPagoId: null };
  }

  if (trigger.startsWith(TRIGGER_TYPES.MENSUALIDAD)) {
    const n = Number(trigger.slice(TRIGGER_TYPES.MENSUALIDAD.length));
    if (!Number.isInteger(n) || n < 1) return { cumple: false, deudaPagoId: null };
    if (mensualidad <= 0) return { cumple: false, deudaPagoId: null };
    // El N-ésimo trigger se cumple cuando los pagos acumulados cubren
    // enganche + N mensualidades regulares.
    const umbral = enganche + n * mensualidad;
    let acumulado = 0;
    for (const p of pagosPositivos) {
      acumulado += Number(p.cantidad || 0);
      if (acumulado >= umbral) {
        return { cumple: true, deudaPagoId: p.id || null };
      }
    }
    return { cumple: false, deudaPagoId: null };
  }

  return { cumple: false, deudaPagoId: null };
}
